// input.js — browser keyboard -> MS2401 matrix, via PC/XT "set 1" make codes.
//
// The bridge's vm86_key_pc(scancode, pressed) looks the scancode up in the ROM
// pc_to_matrix[] table (libvm86/devices/keyb_map.h). That table stores BARE
// set-1 codes (numpad 0x48 etc.) — it does NOT carry the 0xE0 extended prefix —
// so we map the arrow keys onto their numpad equivalents (ArrowUp -> 0x48, the
// numpad-8 code the matrix understands) rather than 0xE048.
//
// Keys must be HELD across BIOS keyboard scans: we press on keydown and release
// on keyup, and the emulator keeps running in between, so a >=200ms physical
// hold naturally straddles multiple INT43h scans.

// KeyboardEvent.code -> IBM PC/XT set-1 make code. Values verified against the
// ROM pc_to_matrix[] table.
export const CODE_TO_SET1 = {
  // number row
  Digit1: 0x02, Digit2: 0x03, Digit3: 0x04, Digit4: 0x05, Digit5: 0x06,
  Digit6: 0x07, Digit7: 0x08, Digit8: 0x09, Digit9: 0x0a, Digit0: 0x0b,
  Minus: 0x0c, Equal: 0x0d,
  // letters
  KeyA: 0x1e, KeyB: 0x30, KeyC: 0x2e, KeyD: 0x20, KeyE: 0x12, KeyF: 0x21,
  KeyG: 0x22, KeyH: 0x23, KeyI: 0x17, KeyJ: 0x24, KeyK: 0x25, KeyL: 0x26,
  KeyM: 0x32, KeyN: 0x31, KeyO: 0x18, KeyP: 0x19, KeyQ: 0x10, KeyR: 0x13,
  KeyS: 0x1f, KeyT: 0x14, KeyU: 0x16, KeyV: 0x2f, KeyW: 0x11, KeyX: 0x2d,
  KeyY: 0x15, KeyZ: 0x2c,
  // punctuation
  BracketLeft: 0x1a, BracketRight: 0x1b, Semicolon: 0x27, Quote: 0x28,
  Backquote: 0x29, Backslash: 0x2b, Comma: 0x33, Period: 0x34, Slash: 0x35,
  // control block
  Escape: 0x01, Backspace: 0x0e, Tab: 0x0f, Enter: 0x1c, Space: 0x39,
  CapsLock: 0x3a, ShiftLeft: 0x2a, ShiftRight: 0x36, ControlLeft: 0x1d,
  Delete: 0x53,
  // function keys
  F1: 0x3b, F2: 0x3c, F3: 0x3d, F4: 0x3e, F5: 0x3f,
  F6: 0x40, F7: 0x41, F8: 0x42, F9: 0x43, F10: 0x44,
  // numeric keypad
  Numpad0: 0x52, Numpad1: 0x4f, Numpad2: 0x50, Numpad3: 0x51, Numpad4: 0x4b,
  Numpad5: 0x4c, Numpad6: 0x4d, Numpad7: 0x47, Numpad8: 0x48, Numpad9: 0x49,
  NumLock: 0x45,
  // arrows -> numpad codes (the matrix has no separate cursor cluster)
  ArrowUp: 0x48, ArrowDown: 0x50, ArrowLeft: 0x4b, ArrowRight: 0x4d,
};

// Alias groups: DISTINCT set-1 codes that resolve to the SAME MS2401 matrix
// cell in keyb_map.h's pc_to_matrix[] must share ONE reference counter, or
// releasing one alias would drop the shared matrix line while the other is
// still held. The only distinct-code collision in the current map is
// Left/Right Shift, which both land on Y6,X0 (0x2a and 0x36). Arrow/numpad
// pairs (ArrowUp/Numpad8 -> 0x48, ArrowLeft/Numpad4 -> 0x4b, ArrowRight/Numpad6
// -> 0x4d, ArrowDown/Numpad2 -> 0x50) already share an IDENTICAL set-1 code, so
// they group naturally by the code value and need no explicit entry here.
// Verified against libvm86/devices/keyb_map.h (only Y6,X0 has two codes).
export const ALIAS_GROUP = new Map([
  [0x36, 0x2a], // Right Shift -> Left Shift group (both -> Y6,X0)
]);

// The reference-counting key for a set-1 code: its alias-group leader, or the
// code itself. The leader is always a real set-1 code that maps to the shared
// matrix cell, so machine.keyPc(group, ...) targets the correct line.
function groupKeyFor(sc) {
  return ALIAS_GROUP.get(sc) ?? sc;
}

export class InputMapper {
  // machine: exposes keyPc(scancode, pressed); onCaptureChange(cb) for UI.
  constructor(machine, captureRegionEl) {
    this.machine = machine;
    this.region = captureRegionEl;
    this.captured = false;
    // Physical browser codes (KeyboardEvent.code) currently held down. Tracks
    // per-key so auto-repeat can't double-count and two aliases of one cell
    // (e.g. ArrowUp + Numpad8, or Left + Right Shift) each count at most once.
    this.heldCodes = new Set();
    // Alias-group key -> number of held codes in that group. The shared matrix
    // line is pressed on 0->1 and released on 1->0 of this count.
    this.groupCounts = new Map();
    this._listeners = [];
    this._install();
  }

  onCaptureChange(cb) { this._listeners.push(cb); }
  _emit() { for (const cb of this._listeners) cb(this.captured); }

  setCaptured(on) {
    if (this.captured === on) return;
    this.captured = on;
    if (!on) this.releaseAll();
    if (this.region) this.region.classList.toggle('is-captured', on);
    this._emit();
  }

  releaseAll() {
    // One release per active group leader (a valid set-1 code -> shared cell).
    for (const group of this.groupCounts.keys()) this.machine.keyPc(group, false);
    this.groupCounts.clear();
    this.heldCodes.clear();
  }

  _install() {
    // Click the display region to capture the keyboard; Esc to release.
    if (this.region) {
      this.region.addEventListener('click', () => this.setCaptured(true));
      this.region.setAttribute('tabindex', '0');
    }

    window.addEventListener('keydown', (e) => {
      if (!this.captured) return;
      // UI text fields (e.g. the disk drawer's rename input) must not feed the
      // guest matrix; these capture-phase handlers run before the field's own,
      // so bail here rather than rely on stopPropagation downstream.
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      // Esc releases capture (and does not reach the guest).
      if (e.code === 'Escape') {
        this.setCaptured(false);
        e.preventDefault();
        return;
      }
      const sc = CODE_TO_SET1[e.code];
      if (sc === undefined) return;
      e.preventDefault();
      if (this.heldCodes.has(e.code)) return; // ignore auto-repeat
      this.heldCodes.add(e.code);
      const group = groupKeyFor(sc);
      const n = (this.groupCounts.get(group) ?? 0) + 1;
      this.groupCounts.set(group, n);
      if (n === 1) this.machine.keyPc(sc, true); // press only on 0->1
    }, true);

    window.addEventListener('keyup', (e) => {
      if (!this.captured) return;
      // Same editable-target bail as keydown: UI text fields own their keys.
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const sc = CODE_TO_SET1[e.code];
      if (sc === undefined) return;
      e.preventDefault();
      if (!this.heldCodes.has(e.code)) return;
      this.heldCodes.delete(e.code);
      const group = groupKeyFor(sc);
      const n = (this.groupCounts.get(group) ?? 1) - 1;
      if (n <= 0) {
        this.groupCounts.delete(group);
        this.machine.keyPc(sc, false); // release only on 1->0
      } else {
        this.groupCounts.set(group, n);
      }
    }, true);

    // Losing focus / tab hidden: drop all held keys so nothing sticks.
    window.addEventListener('blur', () => this.releaseAll());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.releaseAll();
    });
  }
}
